import React, { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { createWithFile } from "../services/apiService";
import SupportTicketForm from "../components/SupportTicket";
import { useToast } from "../hooks/use-toast";
import successIcon from "../assets/images/support/contact.svg";

const Create = ({ onCreateSuccess, userId, onDone }) => {
  const { toast } = useToast();
  const [loadingTicket, setLoadingTicket] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const handleFinalSubmit = async (values) => {
    setLoadingTicket(true);
    try {
      const formData = new FormData();
      formData.append("userId", userId);
      for (const [key, value] of Object.entries(values)) {
        if (key === "image") {
          if (Array.isArray(value?.fileList)) {
            for (const fileObj of value.fileList) {
              if (fileObj.originFileObj) {
                formData.append("images", fileObj.originFileObj);
              }
            }
          }
        } else if (typeof value === "object" && value !== null) {
          for (const [subKey, subValue] of Object.entries(value)) {
            formData.append(`${key}.${subKey}`, subValue);
          }
        } else {
          formData.append(key, value);
        }
      }

      const response = await createWithFile(
        "/api/v1/support-tickets/create",
        formData
      );

      if (response.status === 201 && response.data.data) {
        onCreateSuccess(response.data.data);
        setShowSuccess(true);
      } else {
        toast({
          description: "Could not create ticket. Please try again.",
          variant: "error",
        });
      }
    } catch {
      toast({
        description: "Could not create ticket. Please try again.",
        variant: "error",
      });
    } finally {
      setLoadingTicket(false);
    }
  };

  if (showSuccess) {
    return (
      <div className="z-10 lg:mt-[1.5rem] mt-10 lg:mx-0 mx-5 flex flex-col items-center justify-center">
        <div className="lg:w-[428px] w-full flex flex-col items-center justify-center text-center lg:min-h-[70vh] lg:bg-transparent bg-white rounded-2xl lg:p-0 p-5 mx-auto">
          <img
            src={successIcon}
            alt="Success"
            className="w-[194px] h-[136px] lg:mb-12 mb-8"
          />
          <div className="lg:w-[370px] w-[337px] mb-6">
            <h2 className="font-plusjakarta font-semibold text-[32px] leading-[40px] text-[#33125F] mb-6">
              Message Sent
            </h2>
            <p className="font-plusjakarta font-normal text-[16px] leading-[24px] text-[#423E51] mb-4">
              Thanks for getting in touch! We've received your message and will
              get back to you as soon as we can.
            </p>
            <p className="font-plusjakarta font-normal text-[16px] leading-[24px] text-[#423E51] mb-8">
              In the meantime, feel free to explore the app or check our Help
              Center.
            </p>
          </div>
          <button
            type="button"
            onClick={() => (onDone ? onDone() : setShowSuccess(false))}
            className="bg-[linear-gradient(86.72deg,#57249C_-10.66%,#EB3366_113.47%)] text-white font-semibold text-[14px] px-8 py-[11px] rounded-full w-full lg:w-[309px] transition duration-300 ease-in-out hover:scale-[1.02]"
          >
            Done
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="z-10 mt-[1.5rem] lg:mx-0 mx-5 flex flex-col items-center justify-center">
      <div className="lg:hidden w-full bg-white rounded-2xl shadow-sm overflow-hidden">
        <div className="flex items-center space-x-4 px-3 py-4">
          <button
            type="button"
            onClick={() => onDone?.()}
            className="w-12 h-12 flex items-center justify-center rounded-full border border-[#EBE9EF] bg-transparent text-[#33125F]"
            aria-label="Back"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <p className="font-plusjakarta font-semibold text-[16px] text-[#33125F]">
            Support
          </p>
        </div>
        <div className="px-3 pt-2 pb-8">
          <h2 className="font-plusjakarta font-semibold text-[20px] leading-[32px] text-[#33125F] mb-4">
            Get in touch
          </h2>
          <SupportTicketForm
            onFinish={handleFinalSubmit}
            loadingTicket={loadingTicket}
          />
        </div>
      </div>

      <div className="hidden lg:block lg:max-w-[628px] w-full">
        <h2 className="font-plusjakarta font-semibold text-[32px] leading-[40px] tracking-normal text-left text-[#33125F] my-5">
          We're here to help
        </h2>
        <SupportTicketForm
          onFinish={handleFinalSubmit}
          loadingTicket={loadingTicket}
        />
      </div>
    </div>
  );
};

export default Create;
